import React, { useContext, useState } from 'react';
import { Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import PageHeader from '../../../../shared/components/PageHeader';
import OperationContext from '../context';
import ToolkitsContext from './context';
import { Toolkit } from '../../../../services/types/Toolkit';
import Section from '../../../../shared/components/Section';
import { CheckValue } from '../../../../services/types/Misc';
import VersionTable from './VersionTable';
import EmptyHistory from './EmptyHistory';
import Context from '../../../App/MainProvider/context';
import operationService from '../../../../services/operationService';
import { Button, SpinnerArea } from '../../../../shared/components';
import { safeFind } from '../../../../shared/misc/utils';

const ToolkitList: React.FC = () => {
  const { operation } = useContext(OperationContext);
  if (operation.isEspf) {
    return <Navigate to={`/operations/${operation.number}/toolkit/espf/list`} />;
  }
  const { toolkits } = useContext(ToolkitsContext);
  const { service } = useContext(Context);
  const { t } = useTranslation();
  const sortByVersion = (a: Toolkit, b: Toolkit) => {
    // Primero por version (descendente)
    if (b.version !== a.version) {
      return b.version - a.version;
    }

    // Si la version es igual, ordenar por createdOn (descendente)
    const dateA = new Date(a.modifiedOn);
    const dateB = new Date(b.modifiedOn);
    return dateB.getTime() - dateA.getTime();
  };
  const navigate = useNavigate();
  const hasDraft = toolkits.some((x) => x.pdfCreatedBy === null && !x.isDeleted);
  const toolkitVersions: Toolkit[] = toolkits.filter((x) => x.pdfCreatedBy != null);
  toolkitVersions.sort(sortByVersion);

  toolkits.sort(sortByVersion);

  const lastVersion: Toolkit[] = toolkits.filter((x) => !x.isDeleted).filter((x, i) => i === 0);
  const lastVersionId = lastVersion && lastVersion.length > 0 ? lastVersion[0].id : 0;
  const otherVersions: Toolkit[] = toolkits.filter((x) => x.id !== lastVersionId);

  const legacyConvergence = 'Legacy_Convergence';

  const initialState = toolkits.map((toolkit) => ({
    toolkitId: toolkit.id,
    languages: toolkit.documentList.map(
      (doc, i) =>
        ({
          id: toolkit.id.toString() + i.toString(),
          checked: false,
          text: doc,
        } as CheckValue)
    ),
  }));
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(initialState);
  const handleChange = (id: string, value: boolean, toolkitId: number) => {
    const clone = [...state];
    const toolkitLanguages = safeFind(clone, (x) => x.toolkitId === toolkitId);
    const language = safeFind(toolkitLanguages.languages, (x) => x.id === id);
    language.checked = value;
    setState(clone);
  };
  const handleDownloadClick = (toolkitId: number) => {
    const toolkit = toolkits.find((x) => x.id === toolkitId);
    if (!toolkit) {
      return;
    }

    const dto = { toolkitId: toolkit.id, language: 'English', documentType: 'SF' };
    service(() => operationService.downloadToolkitDocument(dto), { loader: setLoading });

    // PREVIOUS LANGUAGE HANDLING CODE IS PRESENT IN GIT HISTORY HERE
  };

  const handleDownloadClickEzshare = (ezShareCode: string) => {
    // const toolkit = toolkits.find(x => x.createdBy === ezShareCode);
    // if (!toolkit) return;

    window.open(`https://idbdocs.iadb.org/wsdocs/getdocument.aspx?docnum=${ezShareCode}`, '_blank');
    // PREVIOUS LANGUAGE HANDLING CODE IS PRESENT IN GIT HISTORY HERE
  };

  const confirmCreation = () => {
    // setModalOpened(false);
    if (lastVersionId) {
      service(() => operationService.createToolkitDraftFromVersion(lastVersionId), {
        callback: () => {
          // setIsSubmitting(false);
          //  notify('Toolkit', t('successCreatedDraft'), 'success');
          // reloadNotifications();
          navigate(`/operations/${operation.number}/toolkit/form`);
        },
      });
    }
  };

  return (
    toolkitVersions.length > 0 && (
      <SpinnerArea loading={loading}>
        <Container className="dashboard">
          <PageHeader
            backTimes={hasDraft ? 1 : 2}
            title={`Operation ${operation.number} / Toolkit`}
          />
          <Row>
            <Section
              title={
                <>
                  <h5>{t('currentVersion')}</h5>
                  {lastVersionId && lastVersion[0].source === legacyConvergence && (
                    <div>
                      <Button
                        key="btnNewVersion"
                        className="btn btn-secondary px-4 py-1"
                        text={t('New')}
                        onClick={() => confirmCreation()}
                      />
                    </div>
                  )}
                </>
              }
            >
              <VersionTable
                operation={operation}
                toolkits={lastVersion}
                availableLanguages={[...state].filter((x) => x.toolkitId === lastVersionId)}
                handleChange={handleChange}
                handleDownloadClick={handleDownloadClick}
                handleDownloadClickEzshare={handleDownloadClickEzshare}
                className="last-version-content"
              />
            </Section>
            <Section title={t('history')}>
              {otherVersions.length > 0 ? (
                <VersionTable
                  operation={operation}
                  toolkits={otherVersions}
                  availableLanguages={[...state].filter((x) => x.toolkitId !== lastVersionId)}
                  handleChange={handleChange}
                  handleDownloadClick={handleDownloadClick}
                  handleDownloadClickEzshare={handleDownloadClickEzshare}
                  className="history-content"
                />
              ) : (
                <EmptyHistory />
              )}
            </Section>
          </Row>
        </Container>
      </SpinnerArea>
    )
  );
};

export default ToolkitList;
